import { useEffect, useRef, useState } from "react";
import { beginPlanningUpdateActivity } from "../utils/planningMutationActivity.js";
import { isMissingLatestOptimizationSnapshot } from "../utils/resultsPageState.js";

// Les réglages rendent une révision seule : attendre le planning réellement installé.
export default function usePlanningSettingRefresh(results) {
  const scope = `${results?.dispatch_date || ""}:${results?.ui_dispatch_period || results?.dispatch_period || "day"}`;
  const revision = String(results?.runtime_revision || "");
  const pending = useRef(null);
  const mounted = useRef(false);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const [waitingScope, setWaitingScope] = useState("");
  const [error, setError] = useState("");

  const release = () => {
    if (!pending.current) return;
    clearTimeout(pending.current.timer);
    pending.current.finish();
    pending.current = null;
  };

  useEffect(() => {
    mounted.current = true;
    setWaitingScope("");
    setError("");
    return () => {
      mounted.current = false;
      release();
    };
  }, [scope]);

  useEffect(() => {
    const wait = pending.current;
    if (!wait || wait.scope !== scope || results === wait.initialResults || !revision) return;
    // Une lecture ancienne ne suffit pas. Une révision ultérieure peut inclure
    // la synchronisation des métadonnées déclenchée par le rechargement.
    if (revision === wait.previousRevision && revision !== wait.savedRevision) return;
    release();
    setWaitingScope("");
    setError("");
  }, [results, revision, scope, waitingScope]);

  const waitForRefresh = (savedRevision) => {
    if (!mounted.current || currentScope.current !== scope || pending.current) return;
    // Avant le premier calcul, aucune tournée périmée ne peut être modifiée.
    if (isMissingLatestOptimizationSnapshot(results)) return;
    // L'activité de mise à jour autorise les lectures et leur synchronisation
    // interne, tout en refusant les nouvelles écritures de l'utilisateur.
    const wait = { scope, initialResults: results, previousRevision: revision, savedRevision,
      finish: beginPlanningUpdateActivity() };
    pending.current = wait;
    setWaitingScope(scope);
    wait.timer = setTimeout(() => {
      if (pending.current === wait) setError("Le réglage est enregistré, mais l’actualisation du Dispatch n’est pas terminée. Rechargez la page avant de continuer.");
    }, 20000);
  };

  return { isRefreshing: waitingScope === scope, error: waitingScope === scope ? error : "", waitForRefresh };
}
